/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import { TESTIMONIAL_DISPLAY_STYLES, TestimonialItem, TestimonialsModule } from '@coursera-web/cds-labs';

import type { ConsumerPathwayCareerAcademyPageDataContextType } from 'bundles/unified-career-academy/contexts/ConsumerPathwayCareerAcademyPageDataContext';
import type { ConsumerPathwayRolePageDataContextType } from 'bundles/unified-career-academy/contexts/ConsumerPathwayRolePageDataContext';
import CustomTestimonialItem from 'bundles/unified-common/modules/testimonials/CustomTestimonialItem';

type Props = {
  pageData: ConsumerPathwayRolePageDataContextType | ConsumerPathwayCareerAcademyPageDataContextType;
};

export function TestimonialsModuleRenderer({ pageData }: Props) {
  const { testimonials } = pageData?.contentfulContent ?? {};

  if (!testimonials) {
    return null;
  }

  let convertedTestimonials: JSX.Element[] = [];

  if (testimonials.displayStyle === TESTIMONIAL_DISPLAY_STYLES.CAROUSEL) {
    convertedTestimonials =
      testimonials.testimonialsCollection?.items
        ?.map((testimonial) => {
          if (!testimonial) return null;
          const {
            author,
            authorOrganization,
            authorTitle,
            image,
            sys,
            testimonial: quote,
            associatedProduct,
            video,
          } = testimonial;
          return (
            <CustomTestimonialItem
              id={sys.id}
              key={sys.id}
              authorName={author}
              authorOrganization={authorOrganization?.name}
              authorTitle={authorTitle}
              image={image}
              quote={quote}
              productName={associatedProduct}
              videoEmbedUrl={video?.url}
              videoPlaceholderImage={video?.url ? image : undefined}
            />
          );
        })
        .filter(Boolean) ?? [];
  } else {
    convertedTestimonials =
      testimonials.testimonialsCollection?.items
        ?.map((testimonial) => {
          if (!testimonial) return null;
          const { author, authorTitle, image, sys, testimonial: quote, associatedProduct } = testimonial;
          return (
            <TestimonialItem
              key={sys.id}
              name={author}
              imageSrc={image?.url}
              quote={quote}
              productName={associatedProduct}
              learnerSince={Number(authorTitle?.match(/\d+/)?.[0])}
            />
          );
        })
        .filter(Boolean) ?? [];
  }

  if (!convertedTestimonials.length) {
    return null;
  }

  const propsToChild = {
    testimonialItems: convertedTestimonials,
    headline: testimonials.headline,
    displayStyle: testimonials.displayStyle,
    lazyLoadImage: true,
    removeTopPadding: true,
  };

  return <TestimonialsModule {...propsToChild} />;
}

export default TestimonialsModuleRenderer;
